// ==========================================
// 1. 定義資料型別 (Types)
// ==========================================
export interface Product {
  id: number;
  name: string;
  nameEn: string;
  originalPrice: number;
  salePrice: number;
  discount: number;
  image: string;
  platform?: string;
  releaseDate?: string;
  developer?: string;
  publisher?: string;
  steamDeck?: string;
  steamController?: string;
  description?: string;
  languages?: string[];
}

export interface User {
  id: number;
  email: string;
  nickname: string | null;
}

export interface LoginResponse {
  message: string;
  token: string;
  user: User;
}

// 新增：定義序號庫的資料型別
export interface KeyEntry {
  id: number;
  date: string;
  gameName: string;
  gameNameEn: string;
  key: string;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// ==========================================
// 2. API 服務方法 (Service Methods)
// ==========================================
export const api = {
  // --- 原有功能：商品相關 API ---
  async getProducts(): Promise<Product[]> {
    const response = await fetch(`${API_BASE_URL}/products`);
    if (!response.ok) throw new Error('無法取得商品資料');
    return response.json();
  },
  
  async getProductById(id: number): Promise<Product> {
    const response = await fetch(`${API_BASE_URL}/products/${id}`);
    if (!response.ok) throw new Error('找不到該商品');
    return response.json();
  },

  // --- 原有功能：會員驗證相關 API ---
  async sendOtp(email: string): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/send-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || '發送驗證碼失敗'); 
    return data;
  },
  
  async loginWithOtp(email: string, otp: string): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, otp }),
    });
    
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || '登入失敗');
    return data;
  },

  // --- 新增功能：訂閱電子報 API ---
  async subscribeNewsletter(email: string): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.message || '訂閱失敗');
    return data;
  },

  // --- 新增功能：會員專屬資料 API ---
  
  // 取得會員購買的序號庫 (需要帶入 JWT Token)
  async getMyKeys(token: string): Promise<KeyEntry[]> {
    const response = await fetch(`${API_BASE_URL}/member/keys`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` // 夾帶 JWT 憑證進行身分驗證
      }
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.message || '無法取得序號列表');
    return data;
  },

  // --- 擴充：Google 登入 API ---
  async loginWithGoogle(accessToken: string): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accessToken }),
    });
    
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Google 登入失敗');
    return data;
  },

  // --- 新增：取得使用者載具資訊 ---
  async getInvoiceCarriers(token: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/member/settings`, {
      headers: { 
        'Authorization': `Bearer ${token}` 
      }
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data.data;
  },

  // --- 修改：結帳時順便記憶載具 (支援統編) ---
  async saveInvoiceCarrier(token: string, payload: any): Promise<void> {
    await fetch(`${API_BASE_URL}/member/carrier`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload) 
    });
  },

  // --- 新增/更新：送出訂單 ---
  async submitOrder(token: string, orderData: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/checkout`, { 
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(orderData)
    });
    
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.message || '結帳失敗');
    }
    return response.json();
  },

  // --- 核心擴充：查詢訂單狀態 (供結果頁輪詢使用) ---
  async getOrderStatus(token: string, orderNo: string): Promise<{ status: string }> {
    const response = await fetch(`${API_BASE_URL}/checkout/status/${orderNo}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.message || '查詢訂單狀態失敗');
    }
    return response.json();
  }

};